import React, { useState, useEffect } from "react";
import { fetchSettings, saveSettings } from "@/shared/api/services/settingsService";
import { motion } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { useToast } from "@/shared/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import {
  Settings as SettingsIcon,
  Shield,
  Bell,
  Globe,
  Zap,
  Save,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";

export default function Settings() {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await fetchSettings();
      setSettings(data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("failedToLoadSettings"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await saveSettings(settings);
      setSettings(updated);
      toast({
        title: t("success"),
        description: t("settingsSavedSuccessfully"),
        duration: 2000,
      });
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast({
        variant: "destructive",
        title: t("error"),
        description: t("failedToSaveSettings"),
        duration: 2000,
      });
    } finally {
      setSaving(false);
    }
  };





  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Skeleton width={200} height={32} />
            <Skeleton width={150} height={16} className="mt-2" />
          </div>
          <Skeleton width={140} height={40} />
        </div>

        <div className="space-y-6">
          <div className="flex gap-2 p-1 border rounded-lg bg-white w-full overflow-hidden">
            <Skeleton width={100} height={36} />
            <Skeleton width={100} height={36} />
            <Skeleton width={100} height={36} />
            <Skeleton width={100} height={36} />
            <Skeleton width={100} height={36} />
          </div>

          <Card>
            <CardHeader>
              <Skeleton width={150} height={24} />
              <Skeleton width={250} height={16} className="mt-2" />
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Skeleton width={100} height={14} />
                  <Skeleton height={40} />
                </div>
                <div className="space-y-2">
                  <Skeleton width={100} height={14} />
                  <Skeleton height={40} />
                </div>
              </div>
              <div className="space-y-2">
                <Skeleton width={100} height={14} />
                <Skeleton width={200} height={40} />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <h3 className="text-lg font-semibold">{t("failedToLoadSettings")}</h3>
        <Button onClick={loadSettings} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          {t("tryAgain")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{t("settingsTitle")}</h1>
          <p className="text-slate-500">{t("platformConfig")}</p>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full sm:w-auto gap-2"
        >
          {saving ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saving ? t("saving") : t("saveChanges")}
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="w-full overflow-x-auto justify-start whitespace-nowrap bg-white p-1 border h-auto">
          <TabsTrigger value="general" className="gap-2 shrink-0">
            <SettingsIcon className="h-4 w-4" />
            <span>{t("general")}</span>
          </TabsTrigger>

          <TabsTrigger value="security" className="gap-2 shrink-0">
            <Shield className="h-4 w-4" />
            <span>{t("security")}</span>
          </TabsTrigger>

          <TabsTrigger value="callbacks" className="gap-2 shrink-0">
            <Zap className="h-4 w-4" />
            <span>{t("callbacks")}</span>
          </TabsTrigger>

          <TabsTrigger value="qr" className="gap-2 shrink-0">
            <Globe className="h-4 w-4" />
            <span>{t("qrSettings")}</span>
          </TabsTrigger>

          <TabsTrigger value="notifications" className="gap-2 shrink-0">
            <Bell className="h-4 w-4" />
            <span>{t("notificationsSettings")}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>{t("generalSettings")}</CardTitle>
                <CardDescription>{t("basicPlatformConfig")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{t("platformName")}</Label>
                    <Input
                      value={settings.platformName || ""}
                      onChange={(e) => handleChange("platformName", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t("timezone")}</Label>
                    <Select
                      value={settings.timezone || "Asia/Bangkok"}
                      onValueChange={(v) => handleChange("timezone", v)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Asia/Bangkok">Asia/Bangkok (ICT)</SelectItem>
                        <SelectItem value="UTC">UTC</SelectItem>
                        <SelectItem value="Asia/Singapore">Asia/Singapore</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t("defaultCurrency")}</Label>
                  <Select
                    value={settings.defaultCurrency || "PKR"}
                    onValueChange={(v) => handleChange("defaultCurrency", v)}
                  >
                    <SelectTrigger className="w-full sm:w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PKR">PKR - Thai Baht</SelectItem>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="security">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>{t("securitySettings")}</CardTitle>
                <CardDescription>{t("apiSecurityConfig")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label>{t("requireIpWhitelist")}</Label>
                    <p className="text-sm text-slate-500">{t("acceptApiCallsFromWhitelistedIps")}</p>
                  </div>
                  <Switch
                    checked={!!settings.requireIPWhitelist}
                    onCheckedChange={(v) => handleChange("requireIPWhitelist", v)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{t("hmacAlgorithm")}</Label>
                    <Select
                      value={settings.hmacAlgorithm || "sha256"}
                      onValueChange={(v) => handleChange("hmacAlgorithm", v)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sha256">SHA-256</SelectItem>
                        <SelectItem value="sha512">SHA-512</SelectItem>
                        <SelectItem value="sha1">SHA-1 (legacy)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{t("signatureHeaderName")}</Label>
                    <Input
                      value={settings.signatureHeaderName || ""}
                      onChange={(e) => handleChange("signatureHeaderName", e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t("timestampTolerance")}</Label>
                  <Input
                    type="number"
                    value={settings.timestampTolerance || 300}
                    onChange={(e) => handleChange("timestampTolerance", parseInt(e.target.value) || 0)}
                  />
                  <p className="text-xs text-slate-500">{t("rejectOldRequests")}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="callbacks">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>{t("callbackSettings")}</CardTitle>
                <CardDescription>{t("configureCallback")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label>{t("enableCallbackRetry")}</Label>
                    <p className="text-sm text-slate-500">{t("autoRetryFailedCallbacks")}</p>
                  </div>
                  <Switch
                    checked={!!settings.callbackRetryEnabled}
                    onCheckedChange={(v) => handleChange("callbackRetryEnabled", v)}
                  />
                </div>

                {settings.callbackRetryEnabled && (
                  <>
                    <div className="space-y-2">
                      <Label>{t("maxRetryAttempts")}</Label>
                      <Input
                        type="number"
                        value={settings.callbackMaxRetries || 0}
                        onChange={(e) => handleChange("callbackMaxRetries", parseInt(e.target.value) || 0)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>{t("retryDelaySchedule")}</Label>
                      <Input
                        value={Array.isArray(settings.callbackRetryDelay) ? settings.callbackRetryDelay.join(", ") : ""}
                        onChange={(e) =>
                          handleChange(
                            "callbackRetryDelay",
                            e.target.value.split(",").map((v) => parseInt(v.trim()) || 0)
                          )
                        }
                      />
                      <p className="text-xs text-slate-500">
                        {t("settingRetryDelayScheduleNote")}
                      </p>
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>{t("callbackTimeout")}</Label>
                  <Input
                    type="number"
                    value={settings.callbackTimeout || 0}
                    onChange={(e) => handleChange("callbackTimeout", parseInt(e.target.value) || 0)}
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="qr">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>{t("qrPaymentSettings")}</CardTitle>
                <CardDescription>{t("promptPayConfig")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>{t("defaultQrExpiry")}</Label>
                  <Input
                    type="number"
                    value={settings.qrDefaultExpiry || 0}
                    onChange={(e) => handleChange("qrDefaultExpiry", parseInt(e.target.value) || 0)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{t("minimumAmount")}</Label>
                    <Input
                      type="number"
                      value={settings.qrMinAmount || 0}
                      onChange={(e) => handleChange("qrMinAmount", parseInt(e.target.value) || 0)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t("maximumAmount")}</Label>
                    <Input
                      type="number"
                      value={settings.qrMaxAmount || 0}
                      onChange={(e) => handleChange("qrMaxAmount", parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex gap-3">
                    <Globe className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900">{t("supportedBanks")}</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        SCB, KBank, BBL, Krungsri, TTB - {t("enableInProviderSettings")}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        <TabsContent value="notifications">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>{t("notificationSettings")}</CardTitle>
                <CardDescription>{t("configureAlerts")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label>{t("emailNotifications")}</Label>
                    <p className="text-sm text-slate-500">{t("sendAlertsViaEmail")}</p>
                  </div>
                  <Switch
                    checked={!!settings.emailNotifications}
                    onCheckedChange={(v) => handleChange("emailNotifications", v)}
                  />
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label>{t("slackNotifications")}</Label>
                    <p className="text-sm text-slate-500">{t("sendAlertsToSlack")}</p>
                  </div>
                  <Switch
                    checked={!!settings.slackNotifications}
                    onCheckedChange={(v) => handleChange("slackNotifications", v)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t("alertThreshold")}</Label>
                  <Input
                    type="number"
                    value={settings.alertThreshold || 0}
                    onChange={(e) => handleChange("alertThreshold", parseInt(e.target.value) || 0)}
                  />
                  <p className="text-xs text-slate-500">{t("sendAlertAboveAmount")}</p>
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-900">{t("alertTypes")}</h4>
                      <ul className="text-sm text-amber-700 mt-1 space-y-1">
                        <li>• {t("highValueTransactions")}</li>
                        <li>• {t("providerFailures")}</li>
                        <li>• {t("failedCallbackRetries")}</li>
                        <li>• {t("suspiciousActivity")}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>
    </div>
  );
}